import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Modal,
  Spinner,
  Table,
} from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import jwt_decode from 'jwt-decode';
import AiDictionaryApi from '../../../api/AiDictionaryApi';

const EXAMPLE_TERMS = [
  { term: 'company', definition: 'Company or organization; use table "account".' },
  { term: 'deal', definition: 'Sales deal; use table "opportunity". Key field: stage.' },
  { term: 'customer', definition: 'Customer means Account.' },
];

const emptyForm = () => ({
  term: '',
  definition: '',
  is_active: true,
});

const AiDictionaryPage = () => {
  const isAdmin = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      const decoded = jwt_decode(token);
      return String(decoded?.role_name || '').toUpperCase() === 'ADMIN';
    } catch {
      return false;
    }
  }, []);

  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ variant: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingOriginalTerm, setEditingOriginalTerm] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const loadTerms = useCallback(async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const res = await AiDictionaryApi.list();
      if (!res?.success) {
        throw new Error(res?.message || 'Failed to load dictionary.');
      }
      setTerms(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setMessage({ variant: 'danger', text: err.message || 'Failed to load dictionary.' });
      if (!silent) {
        setTerms([]);
      }
    } finally {
      if (silent) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadTerms();
  }, [loadTerms]);

  const openCreate = () => {
    setEditingOriginalTerm(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingOriginalTerm(row.term);
    setForm({
      term: row.term,
      definition: row.definition,
      is_active: row.is_active !== false,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditingOriginalTerm(null);
    setForm(emptyForm());
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ variant: '', text: '' });
    const wasEdit = Boolean(editingOriginalTerm);
    try {
      const payload = {
        term: form.term.trim(),
        definition: form.definition.trim(),
        is_active: form.is_active === true,
      };
      const res = wasEdit
        ? await AiDictionaryApi.update(editingOriginalTerm, payload)
        : await AiDictionaryApi.create(payload);
      if (!res?.success) {
        throw new Error(res?.message || 'Save failed.');
      }
      setSaving(false);
      setShowModal(false);
      setEditingOriginalTerm(null);
      setForm(emptyForm());
      setMessage({
        variant: 'success',
        text: wasEdit ? 'Dictionary term updated.' : 'Dictionary term added.',
      });
      await loadTerms({ silent: true });
    } catch (err) {
      setMessage({ variant: 'danger', text: err.message || 'Save failed.' });
      setSaving(false);
    }
  };

  const handleDelete = async (term) => {
    if (!window.confirm(`Delete dictionary term "${term}"?`)) return;
    setMessage({ variant: '', text: '' });
    try {
      const res = await AiDictionaryApi.remove(term);
      if (!res?.success) {
        throw new Error(res?.message || 'Delete failed.');
      }
      setMessage({ variant: 'success', text: 'Dictionary term deleted.' });
      await loadTerms({ silent: true });
    } catch (err) {
      setMessage({ variant: 'danger', text: err.message || 'Delete failed.' });
    }
  };

  const handleToggleActive = async (row) => {
    try {
      const res = await AiDictionaryApi.update(row.term, {
        definition: row.definition,
        is_active: !row.is_active,
      });
      if (!res?.success) {
        throw new Error(res?.message || 'Update failed.');
      }
      await loadTerms({ silent: true });
    } catch (err) {
      setMessage({ variant: 'danger', text: err.message || 'Update failed.' });
    }
  };

  if (!isAdmin) {
    return <Alert variant="warning">Only administrators can manage the AI dictionary.</Alert>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h5 className="mb-1">Manage AI Dictionary</h5>
          <p className="text-muted small mb-0">
            Terms are merged into every AI Chatbot prompt. No re-indexing required after changes.
          </p>
        </div>
        <Button variant="primary" onClick={openCreate} disabled={loading || refreshing}>
          <FaPlus className="me-1" />
          Add term
        </Button>
      </div>

      {message.text && (
        <Alert
          variant={message.variant || 'info'}
          dismissible
          onClose={() => setMessage({ variant: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading dictionary…
        </div>
      ) : terms.length === 0 ? (
        <Alert variant="light" className="border">
          <p className="mb-2">No dictionary terms yet. Examples you can add:</p>
          <ul className="small mb-3">
            {EXAMPLE_TERMS.map((ex) => (
              <li key={ex.term}>
                <strong>{ex.term}</strong> → {ex.definition}
              </li>
            ))}
          </ul>
          <Button variant="outline-primary" size="sm" onClick={openCreate}>
            Add your first term
          </Button>
        </Alert>
      ) : (
        <div className="position-relative">
          {refreshing && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
              style={{ zIndex: 2 }}
            >
              <Spinner animation="border" size="sm" className="me-2" />
              <span className="small text-muted">Updating…</span>
            </div>
          )}
          <Table responsive striped bordered hover className="ai-chatbot-dictionary-table bg-white mb-0">
          <thead>
            <tr>
              <th>Term</th>
              <th>Definition</th>
              <th style={{ width: 90 }}>Active</th>
              <th style={{ width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {terms.map((row) => (
              <tr key={row.id || row.term}>
                <td>
                  <code>{row.term}</code>
                </td>
                <td>{row.definition}</td>
                <td>
                  <Form.Check
                    type="switch"
                    id={`dict-active-${row.term}`}
                    checked={row.is_active !== false}
                    disabled={refreshing}
                    onChange={() => handleToggleActive(row)}
                    label=""
                    aria-label={`Active ${row.term}`}
                  />
                </td>
                <td>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 me-2"
                    title="Edit"
                    disabled={refreshing}
                    onClick={() => openEdit(row)}
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-danger"
                    title="Delete"
                    disabled={refreshing}
                    onClick={() => handleDelete(row.term)}
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        </div>
      )}

      <Modal show={showModal} onHide={closeModal} backdrop={saving ? 'static' : true} keyboard={!saving}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{editingOriginalTerm ? 'Edit term' : 'Add term'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Term</Form.Label>
            <Form.Control
              type="text"
              value={form.term}
              onChange={(e) => setForm((prev) => ({ ...prev, term: e.target.value }))}
              placeholder="e.g. company, deal, customer"
              disabled={saving}
            />
            <Form.Text className="text-muted">
              Stored lowercase. This is the word users might say in chat.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Definition</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={form.definition}
              onChange={(e) => setForm((prev) => ({ ...prev, definition: e.target.value }))}
              placeholder='e.g. Company or organization; use table "account".'
              disabled={saving}
            />
            <Form.Text className="text-muted">
              Explain which table, field, or concept the AI should use in SQL.
            </Form.Text>
          </Form.Group>
          <Form.Check
            type="switch"
            id="dict-form-active"
            label="Active"
            checked={form.is_active === true}
            onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
            disabled={saving}
          />
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end gap-2">
          <Button variant="secondary" onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || !form.term.trim() || !form.definition.trim()}
            className="ai-dictionary-save-btn"
          >
            {saving ? (
              <span className="d-inline-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" role="status" />
                Saving…
              </span>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AiDictionaryPage;
